import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal, flushSync } from 'react-dom';

import { apiFetch } from '../api/client';
import { ApiProblem } from '../api/problem';
import { useSession } from '../session/useSession';
import { problemMessage } from './form';
import { Icon } from './Icon';

/**
 * L'export Excel de TOUS les tableaux (D75, planche 233).
 *
 * Un seul composant, monté dans la coquille, pose un bouton « Excel » au-dessus
 * de chaque tableau (`table.table`) d'un écran ; `data-export="off"` sur le
 * tableau ou un parent l'en dispense. Le clic relit le tableau tel qu'il est
 * affiché (colonnes, ordre, libellés), sans les colonnes d'actions, et envoie
 * les lignes à POST /api/exports/table qui met en forme le classeur.
 *
 * Toutes les lignes filtrées : un écran PAGINÉ inscrit sa source par
 * `useExportPages` et marque son tableau `data-export-key`. L'export relit
 * alors chaque page (100 lignes) par la lecture même de l'écran, avec ses
 * filtres et les droits du compte, la fait afficher le temps d'un rendu
 * synchrone, la relit, puis rend la page d'origine.
 *
 * Les filtres en place (listes déroulantes, recherche, onglet actif) sont
 * rappelés en tête du fichier.
 */

interface Total {
    readonly total: number;
}

interface PagedSource {
    fetch(page: number, size: number): Promise<Total>;
    show(page: Total): void;
    current(): Total | null;
}

const SOURCES = new Map<string, PagedSource>();
const PAGE = 100;
const MAX_ROWS = 50_000;

/** Inscrit la source paginée d'un tableau (`data-export-key={key}`). */
export function useExportPages<P extends Total>(key: string, page: P | null, show: (page: P) => void, fetch: (page: number, size: number) => Promise<P>): void {
    const latest = useRef({ page, show, fetch });
    latest.current = { page, show, fetch };

    useEffect(() => {
        SOURCES.set(key, {
            fetch: (n, size) => latest.current.fetch(n, size),
            show: (p) => latest.current.show(p as P),
            current: () => latest.current.page,
        });

        return () => {
            SOURCES.delete(key);
        };
    }, [key]);
}

/** Les paramètres d'un écran (sa clé d'URL), à une autre page et une autre taille. */
export function withPage(key: string, page: number, size: number): URLSearchParams {
    const params = new URLSearchParams(key);
    params.set('page', String(page));
    params.set('pageSize', String(size));

    return params;
}

/** Le gardien des boutons : suit les tableaux qui apparaissent et disparaissent. */
export function TableExporter(): ReactNode {
    const [hosts, setHosts] = useState<readonly { readonly table: HTMLTableElement; readonly host: HTMLElement }[]>([]);

    useEffect(() => {
        const root = document.querySelector('main.content');

        if (root === null) return undefined;

        const known = new Map<HTMLTableElement, HTMLElement>();
        let frame = 0;

        const sync = (): void => {
            frame = 0;
            let changed = false;

            for (const [table, host] of known) {
                if (!table.isConnected || !exportable(table)) {
                    host.remove();
                    known.delete(table);
                    changed = true;
                }
            }

            root.querySelectorAll<HTMLTableElement>('table.table').forEach((table) => {
                if (known.has(table) || !exportable(table)) return;
                const anchor = table.closest<HTMLElement>('.table-wrap, .data') ?? table;

                if (anchor.parentElement === null) return;
                const host = document.createElement('div');
                host.className = 'xls-bar';
                host.setAttribute('data-export-host', '');
                anchor.parentElement.insertBefore(host, anchor);
                known.set(table, host);
                changed = true;
            });

            if (changed) setHosts([...known].map(([table, host]) => ({ table, host })));
        };

        const observer = new MutationObserver((records) => {
            if (records.every((r) => [...r.addedNodes, ...r.removedNodes].every((n) => n instanceof HTMLElement && n.hasAttribute('data-export-host')))) return;
            if (frame === 0) frame = window.requestAnimationFrame(sync);
        });
        observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-export'] });
        sync();

        return () => {
            observer.disconnect();
            if (frame !== 0) window.cancelAnimationFrame(frame);
            known.forEach((host) => host.remove());
        };
    }, []);

    return <>{hosts.map(({ table, host }, index) => createPortal(<ExcelButton table={table} />, host, `xls-${index}`))}</>;
}

function exportable(table: HTMLTableElement): boolean {
    return table.closest('[data-export="off"]') === null && table.closest('.screen') !== null && table.querySelector('tbody tr') !== null;
}

function ExcelButton({ table }: { readonly table: HTMLTableElement }): ReactNode {
    const { t } = useSession();
    const [busy, setBusy] = useState<string | null>(null);
    const [failure, setFailure] = useState<string | null>(null);

    async function run(): Promise<void> {
        setFailure(null);
        setBusy(t('export.busy'));

        try {
            const key = table.getAttribute('data-export-key');
            const source = key === null ? undefined : SOURCES.get(key);
            const meta = describe(table);
            let sheet: Sheet;

            if (source === undefined) {
                sheet = readTable(table);
            } else {
                sheet = await readPages(key ?? '', source, (done, total) => setBusy(t('export.pages', { done, total })));
            }

            await send(meta.title, meta.filters, sheet);
        } catch (cause) {
            setFailure(problemMessage(t, cause));
        } finally {
            setBusy(null);
        }
    }

    return (
        <>
            <button className="button button--ghost button--small xls-bar__button" type="button" disabled={busy !== null} onClick={() => void run()} title={t('export.hint')}>
                <Icon name="download" size={16} />
                {busy ?? t('export.label')}
            </button>
            {failure === null ? null : (
                <span className="field__error" role="alert">
                    {failure}
                </span>
            )}
        </>
    );
}

export interface Sheet {
    readonly headers: readonly string[];
    readonly rows: readonly (readonly string[])[];
}

/** Toutes les pages d'une source paginée, relues par le rendu de l'écran. */
async function readPages(key: string, source: PagedSource, progress: (done: number, total: number) => void): Promise<Sheet> {
    const original = source.current();
    const first = await source.fetch(1, PAGE);
    const pages = Math.max(1, Math.ceil(Math.min(first.total, MAX_ROWS) / PAGE));
    let headers: readonly string[] = [];
    const rows: (readonly string[])[] = [];

    try {
        for (let n = 1; n <= pages; n++) {
            progress(n, pages);
            const page = n === 1 ? first : await source.fetch(n, PAGE);
            flushSync(() => source.show(page));
            const table = document.querySelector<HTMLTableElement>(`table[data-export-key="${CSS.escape(key)}"]`);

            if (table === null) break;
            const sheet = readTable(table);
            headers = sheet.headers;
            rows.push(...sheet.rows);
        }
    } finally {
        if (original !== null) flushSync(() => source.show(original));
    }

    return { headers, rows };
}

/** Le titre (écran, ou panneau s'il y a plusieurs tableaux) et les filtres en place. */
function describe(table: HTMLTableElement): { readonly title: string; readonly filters: string | null } {
    const screen = table.closest('.screen');
    const screenTitle = clean(screen?.querySelector('.screen__title')?.textContent ?? document.title);
    const panelTitle = clean(table.closest('section, .att-panel')?.querySelector('h2, h3')?.textContent ?? '');
    const several = (screen?.querySelectorAll('table.table').length ?? 1) > 1;
    const title = several && panelTitle !== '' && panelTitle !== screenTitle ? `${screenTitle} · ${panelTitle}` : screenTitle;
    const filters: string[] = [];

    screen?.querySelectorAll<HTMLElement>('select, input[type="search"], [role="tab"][aria-selected="true"], button[aria-pressed="true"]').forEach((field) => {
        if (field.closest('table, dialog, [data-export-host]') !== null) return;

        if (field instanceof HTMLSelectElement) {
            const option = field.selectedOptions.item(0);
            if (option !== null && field.value !== '') filters.push(clean(option.textContent ?? ''));
        } else if (field instanceof HTMLInputElement) {
            if (field.value.trim() !== '') filters.push(`« ${field.value.trim()} »`);
        } else {
            filters.push(clean(field.textContent ?? ''));
        }
    });

    return { title: title === '' ? 'Export' : title, filters: filters.length === 0 ? null : filters.filter((f) => f !== '').join(' · ') };
}

/**
 * Le tableau tel qu'affiché : en-têtes de la dernière ligne d'en-tête, corps et
 * pied ; les fusions (colspan) sont dépliées ; les colonnes d'actions et les
 * colonnes sans en-tête restées vides sont retirées. Un choix de niveau, une
 * liste ou un champ donnent leur valeur.
 */
export function readTable(table: HTMLTableElement): Sheet {
    const clone = table.cloneNode(true) as HTMLTableElement;
    const originals = table.querySelectorAll<HTMLElement>('input, select, textarea');
    clone.querySelectorAll<HTMLElement>('input, select, textarea').forEach((field, index) => {
        const source = originals.item(index);
        let text = '';

        if (source instanceof HTMLSelectElement) text = source.selectedOptions.item(0)?.textContent ?? '';
        else if (source instanceof HTMLInputElement) text = source.type === 'checkbox' || source.type === 'radio' ? (source.checked ? '✓' : '') : source.value;
        else if (source instanceof HTMLTextAreaElement) text = source.value;
        field.replaceWith(document.createTextNode(text));
    });
    clone.querySelectorAll<HTMLElement>('[role="radiogroup"]').forEach((group) => {
        group.replaceWith(document.createTextNode(group.querySelector('[aria-checked="true"]')?.textContent ?? ''));
    });
    clone.querySelectorAll('svg, .visually-hidden, .avatar, .row-actions, [data-export="skip"], [aria-hidden="true"]').forEach((node) => node.remove());

    const box = document.createElement('div');
    box.style.cssText = 'position:fixed;inset-inline-start:-100000px;inset-block-start:0;inline-size:1600px;opacity:0;pointer-events:none';
    box.appendChild(clone);
    document.body.appendChild(box);

    try {
        const headRows = clone.tHead?.rows;
        const headerRow = headRows === undefined || headRows.length === 0 ? null : headRows.item(headRows.length - 1);
        const headers = headerRow === null ? [] : cells(headerRow);
        const actions = headerRow === null ? [] : expand(headerRow, (cell) => cell.classList.contains('table__actions'));
        const rows: string[][] = [];

        for (const body of [...Array.from(clone.tBodies), ...(clone.tFoot === null ? [] : [clone.tFoot])]) {
            for (const row of Array.from(body.rows)) {
                if (row.closest('[hidden]') !== null) continue;
                const values = cells(row);
                if (values.every((v) => v === '')) continue;
                rows.push(values);
            }
        }

        const width = Math.max(headers.length, ...rows.map((r) => r.length));
        const keep: number[] = [];

        for (let i = 0; i < width; i++) {
            if (actions[i] === true) continue;
            if ((headers[i] ?? '') === '' && rows.every((r) => (r[i] ?? '') === '')) continue;
            keep.push(i);
        }

        return { headers: keep.map((i) => headers[i] ?? ''), rows: rows.map((r) => keep.map((i) => r[i] ?? '')) };
    } finally {
        box.remove();
    }
}

function cells(row: HTMLTableRowElement): string[] {
    const values: string[] = [];

    for (const cell of Array.from(row.cells)) {
        values.push(clean(cell.innerText));

        for (let i = 1; i < cell.colSpan; i++) values.push('');
    }

    return values;
}

function expand(row: HTMLTableRowElement, test: (cell: HTMLTableCellElement) => boolean): boolean[] {
    const flags: boolean[] = [];

    for (const cell of Array.from(row.cells)) {
        for (let i = 0; i < Math.max(1, cell.colSpan); i++) flags.push(test(cell));
    }

    return flags;
}

/** Les retours à la ligne d'une cellule (libellé et sous-libellé) deviennent « · ». */
function clean(text: string): string {
    return text
        .split(/\n+/)
        .map((part) => part.replace(/\s+/g, ' ').trim())
        .filter((part) => part !== '')
        .join(' · ');
}

async function send(title: string, filters: string | null, sheet: Sheet): Promise<void> {
    const response = await apiFetch('/api/exports/table', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: '*/*' },
        body: JSON.stringify({ title, filters, headers: sheet.headers, rows: sheet.rows }),
    });

    if (!response.ok) {
        throw await ApiProblem.from(response);
    }

    const disposition = response.headers.get('Content-Disposition') ?? '';
    const name = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition)?.[1] ?? 'export.xlsx';
    const url = URL.createObjectURL(await response.blob());
    const link = document.createElement('a');
    link.href = url;
    link.download = decodeURIComponent(name);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
